import { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search } from "lucide-react";
import { useProducts } from "@/hooks/useProducts";
import { ProductCard } from "@/components/site/ProductCard";
import { MaskLine } from "@/components/site/Reveal";
import { CATEGORIES } from "@/lib/api";
import { COLLECTION_LINKS } from "@/lib/collections";
import { useSeo, SITE_URL } from "@/lib/seo";
import { SubShopBanner } from "@/components/subscription/SubPromo";
import { useLang } from "@/lib/i18n";
import { FlashHero } from "@/components/site/FlashSale";

const HUB_BY_CATEGORY = { Pousse: "/pousse-des-cheveux", Locks: "/locks", Barbe: "/barbe", Kids: "/cheveux-enfants" };

const SHIP = "Livraison gratuite en France via Mondial Relay, dès 11,90 € dans les DOM-TOM, 4,99 € en Europe.";
const SEO = {
  "": { t: "Boutique soins cheveux afro et crépus | Ozentya", d: `Tous les soins capillaires Ozentya : pousse des cheveux, locks, barbe, visage et corps, en packs ou à l'unité. ${SHIP}` },
  pack: { t: "Packs Ozentya — routines de soins capillaires", d: `Packs Ozentya : des routines complètes pour la pousse des cheveux, les locks et la barbe, à prix doux. ${SHIP}` },
  unite: { t: "Soins capillaires à l'unité", d: `Lotions, huiles, shampoings et soins Ozentya à l'unité pour composer votre routine cheveux, locks, barbe, visage et corps. ${SHIP}` },
  Pousse: { t: "Soins pour la pousse des cheveux", d: `Soins Ozentya pour la pousse des cheveux : lotions, huiles et shampoings pour une routine capillaire régulière et un cuir chevelu nourri. ${SHIP}` },
  Locks: { t: "Soins pour locks et dreadlocks", d: `Soins Ozentya pour locks et dreadlocks : gels, huiles et shampoings pour l'entretien, la tenue et l'hydratation de vos locks. ${SHIP}` },
  Barbe: { t: "Soins pour la barbe", d: `Soins Ozentya pour la barbe : huiles, shampoings et routines pour une barbe nourrie, douce et soignée. ${SHIP}` },
  "Soins visage": { t: "Soins visage : sérum anti-taches, crème et masque", d: `Soins visage Ozentya : sérum anti-taches LUMINA V.G. (niacinamide, alpha-arbutine, acide tranexamique), crème visage et masque à l'argile. ${SHIP}` },
  "Soins du corps": { t: "Soins du corps", d: `Soins du corps Ozentya : gel douche à l'aloe vera pour une peau fraîche et douce. ${SHIP}` },
  Kids: { t: "Soins capillaires pour enfants", d: `Soins capillaires Ozentya Kids, pensés pour les cheveux des enfants. ${SHIP}` },
};

const TYPES = [{ v: "", l: "Tout" }, { v: "pack", l: "Packs Ozentya" }, { v: "unite", l: "À l'unité" }];

function Chip({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`h-10 rounded-full border px-5 text-xs uppercase tracking-[0.18em] transition-[background-color,color,border-color] duration-300 ${active ? "border-[var(--gold)] bg-[var(--gold)] text-[var(--ink)]" : "border-[var(--line)] text-[var(--parchment)]/85 hover:border-[var(--gold)]"}`}>
      {children}
    </button>
  );
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const type = params.get("type") || "";
  const raw = params.get("category") || "";
  const category = raw === "Visage & Corps" ? "Soins visage" : raw; // ancienne rubrique scindée en deux
  const [q, setQ] = useState("");
  const { data, isLoading, isError } = useProducts();
  const key = category && SEO[category] ? category : type && SEO[type] ? type : "";
  const seo = SEO[key];
  const qs = category ? `?category=${encodeURIComponent(category)}` : type ? `?type=${type}` : "";
  const hub = HUB_BY_CATEGORY[category];
  const { lang, t } = useLang();
  const en = lang === "en";
  useSeo(en ? {
    title: "Ozentya shop: afro hair, locs & beard care made in France", lang: "en",
    description: "Shop Ozentya afro hair care made in France: scalp care, packs, locs and beard products. Prices in euros, shipped worldwide (Europe €4.99, worldwide from €9.90).",
    path: "/en/shop", jsonLd: { "@context": "https://schema.org", "@type": "CollectionPage", name: "Ozentya shop", url: `${SITE_URL}/en/shop`, inLanguage: "en" },
  } : {
    title: key ? `${seo.t} | Ozentya` : seo.t, description: seo.d, path: hub || `/boutique${qs}`,
    jsonLd: { "@context": "https://schema.org", "@type": "CollectionPage", name: seo.t, url: `${SITE_URL}/boutique${qs}`, description: seo.d },
  });

  const set = (k, v) => {
    const n = new URLSearchParams(params);
    v ? n.set(k, v) : n.delete(k);
    setParams(n);
  };

  const list = useMemo(
    () => (Array.isArray(data) ? data : []).filter((p) => (!type || p.type === type) && (!category || p.category === category)
      && (!q || `${p.name} ${p.en?.name || ""}`.toLowerCase().includes(q.toLowerCase()))),
    [data, type, category, q]
  );

  return (
    <main data-testid="shop-page" className="mx-auto max-w-[1440px] px-5 pb-32 pt-44 md:px-10">
      <FlashHero className="mb-12" />
      <p className="eyebrow mb-6">{en ? "The shop" : "La boutique"}</p>
      <h1 key={`${type}-${category}`} className="font-display text-5xl leading-none md:text-8xl">
        <MaskLine delay={0.05}>{category ? t(category) : (type === "pack" ? t("Packs Ozentya") : type === "unite" ? t("À l'unité") : en ? "Afro & coily hair care" : "Soins cheveux afro et crépus")}</MaskLine>
      </h1>
      <p data-testid="shop-intro" className="mt-6 max-w-2xl text-sm leading-relaxed text-[var(--parchment)]/80 md:text-base">{en ? "Ozentya products are made in France. Prices are in euros (indicative £ / $ on each product page). Shipping: Europe €9.99 by Mondial Relay pickup point, worldwide by Colissimo International from €9.90, calculated at checkout." : seo.d}</p>
      {hub && !en ? <Link to={hub} data-testid="shop-hub-link" className="mt-3 inline-block text-xs uppercase tracking-[0.2em] text-[var(--gold-soft)] underline-offset-4 hover:underline">Conseils, routine et FAQ {category === "Kids" ? "cheveux des enfants" : category.toLowerCase()} →</Link> : null}
      {type !== "pack" && !en ? <SubShopBanner /> : null}
      <div className="mt-12 flex flex-col gap-5 border-y border-[var(--line)] py-6">
        <div className="flex flex-wrap gap-2">
          {TYPES.map((x) => <Chip key={x.l} testid={`filter-type-${x.v || "all"}`} active={type === x.v} onClick={() => set("type", x.v)}>{t(x.l)}</Chip>)}
        </div>
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex flex-wrap gap-2">
            <Chip testid="filter-category-all" active={!category} onClick={() => set("category", "")}>{t("Toutes gammes")}</Chip>
            {CATEGORIES.map((c) => <Chip key={c} testid={`filter-category-${c}`} active={category === c} onClick={() => set("category", c)}>{t(c)}</Chip>)}
            {(en ? [] : COLLECTION_LINKS).map((l) => (
              <Link key={l.to} to={l.to} data-testid={`shop-collection-link-${l.to.slice(1)}`} className="flex h-10 items-center rounded-full border border-[var(--gold)]/50 px-5 text-xs uppercase tracking-[0.18em] text-[var(--gold-soft)] transition-[background-color,color] duration-300 hover:bg-[var(--gold)] hover:text-[var(--ink)]">{t(l.label)}</Link>
            ))}
          </div>
          <label className="flex h-11 items-center gap-3 rounded-full border border-[var(--line)] px-5 md:w-72">
            <Search className="h-4 w-4 text-[var(--muted)]" />
            <input data-testid="shop-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Rechercher un soin")} className="w-full bg-transparent text-sm outline-none placeholder:text-[var(--muted)]" />
          </label>
        </div>
      </div>
      <p data-testid="shop-result-count" className="mt-6 text-sm text-[var(--muted)]">{list.length} {en ? (list.length > 1 ? "products" : "product") : `soin${list.length > 1 ? "s" : ""}`}</p>
      {isLoading && <p className="mt-10 text-[var(--muted)]">{t("Chargement…")}</p>}
      {isError && <p className="mt-10 text-[var(--muted)]">{t("La boutique est momentanément indisponible.")}</p>}
      <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-4">
        {list.map((p, i) => <ProductCard key={p.slug} p={p} index={i} />)}
      </div>
      {!isLoading && !list.length && <p data-testid="shop-empty" className="mt-10 font-display text-2xl italic text-[var(--muted)]">{t("Aucun soin ne correspond à votre recherche.")}</p>}
    </main>
  );
}
